import {
  toBackendLang,
  useRegister,
  useSendEmailCode,
  useTelegramLogin,
} from "@kupikupi/api";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { TgHeader } from "../components/TgHeader";
import { TgMain } from "../components/TgMain";
import { TgPage } from "../components/TgPage";
import { setAuthToken } from "../lib/auth";
import { getInviteCode, getRawInitData } from "../lib/telegram-auth";

// ─────────────────────────────────────────────────────────────────────────────
// Password helpers (inline — mirrors web lib/password-validation.ts logic)
// ─────────────────────────────────────────────────────────────────────────────

type PasswordStrength = "none" | "weak" | "mid" | "strong";
type PasswordError = "required" | "too_short";

function getPasswordStrength(pw: string): PasswordStrength {
  if (!pw) return "none";
  if (pw.length < 6) return "weak";
  if (pw.length < 8) return "mid";
  return "strong";
}

function validatePassword(pw: string): PasswordError | null {
  if (!pw) return "required";
  if (pw.length < 8) return "too_short";
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Invite-code helpers (inline — reads TG start_param + shared LS key)
// ─────────────────────────────────────────────────────────────────────────────

function getStoredInviteCode(): string | null {
  // 1. Telegram start_param (includes LS fallback via getInviteCode)
  try {
    const tgCode = getInviteCode();
    if (tgCode) return tgCode;
  } catch {
    // not in TG env
  }
  // 2. Shared localStorage key
  try {
    if (typeof window !== "undefined") {
      return window.localStorage.getItem("kupikupi-invite-code");
    }
  } catch {
    // storage blocked
  }
  return null;
}

function clearStoredInviteCode(): void {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem("kupikupi-invite-code");
    }
  } catch {
    // ignore
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// RegisterPage
// ─────────────────────────────────────────────────────────────────────────────

export function RegisterPage() {
  const { t, i18n } = useTranslation("auth");
  const navigate = useNavigate();

  // Form state
  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode] = useState<string | null>(getStoredInviteCode);
  const [editableInvite, setEditableInvite] = useState("");
  const [agreed, setAgreed] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<PasswordError | null>(null);
  const [tgError, setTgError] = useState(false);

  // API hooks — no captchaTokenProvider in Mini App (SmartCaptcha is web-only)
  const sendCode = useSendEmailCode();
  const register = useRegister();
  const tgLogin = useTelegramLogin();

  // Derived state
  const pwStrength = getPasswordStrength(password);
  const codeEnabled = sendCode.isSuccess || sendCode.cooldown > 0;

  const sendBtnLabel =
    sendCode.cooldown > 0
      ? t("register.send_code_cooldown", { sec: sendCode.cooldown })
      : sendCode.isPending
        ? t("register.send_code_sending")
        : t("register.send_code");

  const canSubmit =
    !!email &&
    /^\d{6}$/.test(emailCode) &&
    password.length > 0 &&
    agreed &&
    !register.isPending;

  // ── Telegram quick-login ────────────────────────────────────────────────
  const onTelegramLogin = async () => {
    const initData = getRawInitData();
    if (!initData) {
      setTgError(true);
      return;
    }
    try {
      setTgError(false);
      const code = getInviteCode();
      const result = await tgLogin.mutateAsync(
        code ? { initData, inviteCode: code } : { initData },
      );
      if (result.xaccessToken) {
        setAuthToken(result.xaccessToken);
        navigate({ to: "/" as never });
      }
    } catch {
      setTgError(true);
    }
  };

  // ── Send email code ────────────────────────────────────────────────────
  const onSend = async () => {
    if (!email || !sendCode.canSend) return;
    setError(null);
    try {
      await sendCode.mutateAsync({ email, type: 0 });
    } catch (e) {
      setError(e instanceof Error ? e.message : t("register.send_code_failed"));
    }
  };

  // ── Submit registration ────────────────────────────────────────────────
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const pwErr = validatePassword(password);
    setPasswordError(pwErr);
    if (pwErr) return;

    const effectiveInvite = (inviteCode ?? editableInvite.trim()) || undefined;

    try {
      const resp = await register.mutateAsync({
        email,
        emailCode,
        password,
        username: email,
        countryCode: "RU",
        currencyCode: "RUB",
        lang: toBackendLang(i18n.language),
        ...(effectiveInvite ? { inviteCode: effectiveInvite } : {}),
      });
      if (resp?.xaccessToken) {
        clearStoredInviteCode();
        setAuthToken(resp.xaccessToken);
        navigate({ to: "/" as never });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t("register.register_failed"));
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <TgPage tabbar={false}>
      {/* Green gradient background — matches design spec linear-gradient(180deg,#E0F7F1,#B8E8DD) */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "linear-gradient(180deg,#E0F7F1,#B8E8DD)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <TgHeader title={t("register.title")} back borderless />

        {/* ── Scrollable form body ─────────────────────────────────────── */}
        <div className="tg-body" style={{ flex: 1, padding: "8px 20px 120px" }}>
          {/* Hero heading */}
          <h1
            style={{
              fontFamily: "-apple-system,system-ui,sans-serif",
              fontSize: 22,
              fontWeight: 900,
              margin: "0 0 6px",
              lineHeight: 1.15,
            }}
          >
            {t("register.hero_title")}
            <br />
            <span style={{ color: "var(--color-cta)" }}>{t("register.hero_cta")}</span>
          </h1>
          <p style={{ fontSize: 12, color: "var(--color-text-muted)", margin: "0 0 18px" }}>
            {t("register.hero_sub")}
          </p>

          {/* Telegram button */}
          <button
            type="button"
            onClick={onTelegramLogin}
            disabled={tgLogin.isPending}
            style={{
              width: "100%",
              padding: 13,
              background: "#229ED9",
              color: "#fff",
              border: 0,
              borderRadius: 14,
              fontWeight: 800,
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              marginBottom: 8,
              opacity: tgLogin.isPending ? 0.6 : 1,
              cursor: "pointer",
            }}
          >
            ✈ {t("register.telegram_btn")}
          </button>

          {tgError && (
            <p style={{ fontSize: 12, color: "var(--color-danger)", margin: "0 0 8px", textAlign: "center" }}>
              {t("login.login_failed")}
            </p>
          )}

          {/* "или заполните" divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "14px 0" }}>
            <div style={{ flex: 1, height: 1, background: "var(--color-border)" }} />
            <span style={{ fontFamily: "monospace", fontSize: 9, color: "var(--color-text-muted)" }}>
              {t("register.or_fill")}
            </span>
            <div style={{ flex: 1, height: 1, background: "var(--color-border)" }} />
          </div>

          {/* Registration form */}
          <form id="reg-form" onSubmit={onSubmit} noValidate>
            {/* Email + send-code button */}
            <div className="e-field" style={{ marginBottom: 10 }}>
              <label className="e-field__l" htmlFor="reg-email" style={{ fontSize: 11 }}>
                {t("register.email")} <span className="req">*</span>
              </label>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  id="reg-email"
                  className="e-input"
                  type="email"
                  placeholder={t("register.email_placeholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  style={{ flex: 1, height: 40, fontSize: 13 }}
                />
                <button
                  type="button"
                  onClick={onSend}
                  disabled={!email || !sendCode.canSend}
                  style={{
                    whiteSpace: "nowrap",
                    padding: "0 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    background: sendCode.canSend && email ? "var(--color-primary-fill)" : "var(--color-surface)",
                    color: sendCode.canSend && email ? "#fff" : "var(--color-text-muted)",
                    border: "none",
                    borderRadius: 10,
                    cursor: sendCode.canSend && email ? "pointer" : "not-allowed",
                    flexShrink: 0,
                  }}
                >
                  {sendBtnLabel}
                </button>
              </div>
            </div>

            {/* Email code */}
            <div className="e-field" style={{ marginBottom: 10 }}>
              <label className="e-field__l" htmlFor="reg-code" style={{ fontSize: 11 }}>
                {t("register.email_code")} <span className="req">*</span>
              </label>
              <input
                id="reg-code"
                className="e-input"
                inputMode="numeric"
                maxLength={6}
                placeholder={t("register.email_code_placeholder")}
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))}
                disabled={!codeEnabled}
                style={{ height: 40, fontSize: 13 }}
              />
              {!codeEnabled && (
                <span className="e-field__hint">{t("register.email_code_disabled_hint")}</span>
              )}
            </div>

            {/* Password + strength meter */}
            <div className="e-field" style={{ marginBottom: 0 }}>
              <label className="e-field__l" htmlFor="reg-password" style={{ fontSize: 11 }}>
                {t("register.password")} <span className="req">*</span>
              </label>
              <input
                id="reg-password"
                className="e-input"
                type="password"
                placeholder={t("register.password_placeholder")}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError(null);
                }}
                autoComplete="new-password"
                style={{ height: 40, fontSize: 13 }}
              />
              {password.length > 0 && (
                <>
                  <div
                    className={`e-pw-meter e-pw-meter--${pwStrength === "none" ? "weak" : pwStrength}`}
                    style={{ marginTop: 6 }}
                  >
                    <span className="e-pw-meter__seg" />
                    <span className="e-pw-meter__seg" />
                    <span className="e-pw-meter__seg" />
                    <span className="e-pw-meter__seg" />
                  </div>
                  <div className="e-pw-hint" style={{ fontSize: 10 }}>
                    <span className="e-pw-hint__l">{t("register.pw_strength_label")}</span>
                    <span
                      className={`e-pw-hint__v e-pw-hint__v--${pwStrength === "none" ? "weak" : pwStrength}`}
                    >
                      {pwStrength === "strong"
                        ? t("register.pw_strength_strong")
                        : pwStrength === "mid"
                          ? t("register.pw_strength_mid")
                          : t("register.pw_strength_weak")}
                    </span>
                  </div>
                </>
              )}
              {passwordError && (
                <p className="e-field__err" style={{ fontSize: 11 }}>
                  {t(`register.password_${passwordError}`)}
                </p>
              )}
            </div>

            {/* Invite code */}
            <div className="e-field" style={{ marginTop: 10 }}>
              <label className="e-field__l" htmlFor="reg-invite" style={{ fontSize: 11 }}>
                {t("register.invite_code")}{" "}
                <span
                  className="opt"
                  style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 400 }}
                >
                  {t("register.optional")}
                </span>
              </label>
              <input
                id="reg-invite"
                className="e-input"
                placeholder="KK-XXXXXX"
                value={inviteCode ?? editableInvite}
                onChange={inviteCode ? undefined : (e) => setEditableInvite(e.target.value)}
                readOnly={!!inviteCode}
                style={{ height: 40, fontSize: 13 }}
              />
              {inviteCode && (
                <span className="e-field__hint">{t("register.invite_code_locked_hint")}</span>
              )}
            </div>

            {/* Terms checkbox */}
            <label
              style={{
                display: "flex",
                gap: 6,
                marginTop: 14,
                fontSize: 10,
                lineHeight: 1.55,
                cursor: "pointer",
                alignItems: "flex-start",
              }}
            >
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
              />
              <span
                style={{
                  width: 16,
                  height: 16,
                  border: `1.5px solid ${agreed ? "var(--color-primary-fill)" : "var(--color-border-strong)"}`,
                  borderRadius: 4,
                  background: agreed ? "var(--color-primary-fill)" : "transparent",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                {agreed && <span style={{ color: "#fff", fontSize: 9 }}>✓</span>}
              </span>
              <span>
                {t("register.agree_prefix")}
                <a
                  href="#"
                  style={{ color: "var(--color-primary-fill)", fontWeight: 700, textDecoration: "none" }}
                  onClick={(e) => e.preventDefault()}
                >
                  {t("register.agree_terms")}
                </a>
                {t("register.agree_compliance")}
              </span>
            </label>

            {error && (
              <p style={{ color: "var(--color-danger)", fontSize: 12, marginTop: 8 }}>{error}</p>
            )}
          </form>
        </div>

        {/* ── Sticky bottom CTA ─────────────────────────────────────────── */}
        <TgMain
          disabled={!canSubmit}
          onClick={() => {
            const form = document.getElementById("reg-form") as HTMLFormElement | null;
            form?.requestSubmit();
          }}
        >
          {register.isPending ? t("register.submitting") : t("register.cta")}
        </TgMain>
      </div>
    </TgPage>
  );
}
